import React, { useState, useEffect } from "react";

import HTML from "../assets/html.png";
import CSS from "../assets/css.png";
import JavaScript from "../assets/javascript.png";
import ReactImg from "../assets/react.png";
import Node from "../assets/node.png";
import Wordpress from "../assets/Wordpress.png";
import Tailwind from "../assets/tailwind.png";
import Mongo from "../assets/mongo.png";
import Bootstrap from "../assets/bootstrap.png";
import Mysql from "../assets/mysql.png";
import Postgresql from "../assets/postgresql.png";
import Express from "../assets/Expressjs.png";
import Elementor from "../assets/Elementor.png";
import Figma from "../assets/Figma.png";
import VSCode from "../assets/vs code.png";
import Postman from "../assets/postman.png";
import PHP from "../assets/php.png";

import { motion, AnimatePresence } from "framer-motion";

const categories = ["All", "Frontend", "Backend & DB", "CMS & Design", "Tools"];

export const DEFAULT_TECHNOLOGIES = [
  { id: 1, img: HTML, name: "HTML5", category: "Frontend" },
  { id: 2, img: CSS, name: "CSS3", category: "Frontend" },
  { id: 3, img: JavaScript, name: "JavaScript", category: "Frontend" },
  { id: 4, img: Tailwind, name: "Tailwind CSS", category: "Frontend" },
  { id: 5, img: ReactImg, name: "React JS", category: "Frontend" },
  { id: 6, img: Bootstrap, name: "Bootstrap", category: "Frontend" },
  { id: 7, img: Node, name: "Node JS", category: "Backend & DB" },
  { id: 8, img: Express, name: "Express JS", category: "Backend & DB" },
  { id: 9, img: Mongo, name: "MongoDB", category: "Backend & DB" },
  { id: 10, img: Mysql, name: "MySQL", category: "Backend & DB" },
  { id: 11, img: Postgresql, name: "PostgreSQL", category: "Backend & DB" },
  { id: 17, img: PHP, name: "PHP", category: "Backend & DB" },
  { id: 12, img: Wordpress, name: "WordPress", category: "CMS & Design" },
  { id: 13, img: Elementor, name: "Elementor", category: "CMS & Design" },
  { id: 14, img: Figma, name: "Figma", category: "CMS & Design" },
  { id: 15, img: VSCode, name: "VS Code", category: "Tools" },
  { id: 16, img: Postman, name: "Postman", category: "Tools" },
];

export const getSkillsList = () => {
  const stored = localStorage.getItem("custom_skills");
  if (stored) {
    try {
      let storedSkills = JSON.parse(stored);
      const missingInitial = DEFAULT_TECHNOLOGIES.filter(
        (initItem) => !storedSkills.some((s) => s.id === initItem.id || s.name.toLowerCase() === initItem.name.toLowerCase())
      );
      if (missingInitial.length > 0) {
        storedSkills = [...storedSkills, ...missingInitial];
        localStorage.setItem("custom_skills", JSON.stringify(storedSkills));
      }
      return storedSkills;
    } catch (e) {
      return DEFAULT_TECHNOLOGIES;
    }
  }
  return DEFAULT_TECHNOLOGIES;
};

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [skillsList, setSkillsList] = useState(getSkillsList());

  useEffect(() => {
    const handleStorage = () => {
      setSkillsList(getSkillsList());
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const filteredSkills =
    activeCategory === "All"
      ? skillsList
      : skillsList.filter((skill) => skill.category === activeCategory);

  return (
    <section
      name="skills"
      className="relative w-full min-h-screen bg-[#0F172A] text-slate-100 py-24 flex flex-col items-center overflow-hidden"
    >
      {/* Background Glow */}
      <div className="absolute top-1/3 right-10 w-96 h-96 bg-blue-600/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/4 left-10 w-96 h-96 bg-[#00B4DB]/12 rounded-full blur-[140px] pointer-events-none" />

      {/* Section Header */}
      <div className="text-center max-w-3xl mx-auto px-6 mb-12 relative z-10">
        <span className="text-[#00B4DB] text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full bg-[#00B4DB]/10 border border-[#00B4DB]/30">
          Tech Stack & Mastery
        </span>
        <h2 className="text-3xl sm:text-5xl font-extrabold text-white mt-4 tracking-tight">
          Technologies & <span className="gradient-text">Skills</span>
        </h2>
        <p className="text-slate-400 text-sm sm:text-base mt-3">
          Technologies and tools I leverage to build scalable, full-stack applications.
        </p>
      </div>

      {/* Category Pills Bar */}
      <div className="flex justify-center mb-12 px-4 relative z-10">
        <div className="inline-flex flex-wrap justify-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 bg-[#1E293B]/80 backdrop-blur-xl border border-slate-700/80 rounded-2xl shadow-xl shadow-slate-950/40 max-w-full">
          {categories.map((cat) => {
            const isActive = activeCategory === cat;
            return (
              <motion.button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                whileTap={{ scale: 0.96 }}
                className="relative px-4 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-colors duration-200 flex items-center justify-center select-none group"
              >
                {isActive && (
                  <motion.div
                    layoutId="activeSkillTab"
                    className="absolute inset-0 bg-gradient-to-r from-blue-600 via-sky-600 to-[#00B4DB] rounded-xl shadow-md shadow-blue-500/25"
                    transition={{ type: "spring", stiffness: 450, damping: 35 }}
                  />
                )}
                <span
                  className={`relative z-10 transition-colors duration-200 ${
                    isActive ? "text-white font-bold" : "text-slate-300 hover:text-white"
                  }`}
                >
                  {cat}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Skills Flex Container (Centered Cards) */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 15, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -15, scale: 0.98 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="w-full max-w-5xl flex flex-wrap justify-center gap-5 px-6 relative z-10 min-h-[260px]"
        >
          {filteredSkills.map((skill, index) => (
            <motion.div
              initial={{ opacity: 0, y: 15, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 0.2,
                delay: Math.min(index * 0.025, 0.25),
                ease: "easeOut",
              }}
              whileHover={{ y: -5 }}
              key={skill.id || skill.name}
              className="group relative flex flex-col items-center justify-center w-36 sm:w-44 p-5
                         bg-[#1E293B]/85 backdrop-blur-xl border border-slate-700/80 rounded-2xl
                         hover:border-[#00B4DB]/60 hover:bg-[#1E293B]
                         transition-all duration-300 shadow-lg hover:shadow-2xl"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl p-2.5 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 shadow-inner bg-slate-900 border border-slate-700/80">
                <img
                  src={skill.img}
                  alt={skill.name}
                  className="max-w-full max-h-full object-contain filter drop-shadow-sm"
                />
              </div>
              <p className="text-xs sm:text-sm font-semibold text-white group-hover:text-[#00B4DB] transition-colors text-center">
                {skill.name}
              </p>
              <span className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">
                {skill.category}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
    </section>
  );
};

export default Skills;
